import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  first,
  startWith,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'

import { Component, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'

import { SerieProposal, SerieProposalStatus } from '../serie-proposal'
import { serieProposalActions } from '../serie-proposal.actions'
import { serieProposalSelectors } from '../serie-proposal.selectors'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { Dictionary } from '@ngrx/entity'
import { Product } from '../../../product/product'
import { productSelectors } from '../../../product/product.selectors'
import { getEnumAsArray } from 'src/app/shared/until'
import { SerieProposalService } from '../serie-proposal.service'
import { memberSelectors } from '../../../member/member.selectors'
import { Member } from '../../../member/member'
import { Router } from '@angular/router'
import { NotificationService } from 'src/app/core/notification.service'
import { Actions, ofType } from '@ngrx/effects'
import {
  SerieProposalItem,
  SerieProposalItemStatus
} from '../serie-proposal-item/serie-proposal-item'
import { serieProposalItemActions } from '../serie-proposal-item/serie-proposal-item.actions'
import { serieProposalItemSelectors } from '../serie-proposal-item/serie-proposal-item.selectors'
import { MessageService } from 'src/app/features/message/message.service'
import { SerieProposalInitialRequestModalComponent } from '../initial-request-modal/initial-request-modal.component'
import { BsModalService } from 'ngx-bootstrap/modal'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { WCAG_TITLE_TRANSLATIONS } from 'src/app/shared/constants'

@Component({
  selector: 'serie-proposal-show',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SerieProposalShowComponent implements OnInit {
  public serieProposal$: Observable<SerieProposal>
  public serieProposalId$: Observable<number>
  public isLoading$: Observable<boolean>
  public selectedLanguage$: Observable<string>
  public serieProposalItemEntities$: Observable<Dictionary<SerieProposalItem>>
  public productEntities$: Observable<Dictionary<Product>>
  public memberEntities$: Observable<Dictionary<Member>>
  public serieProposalStatus = SerieProposalStatus
  public serieProposalStatuses = getEnumAsArray(SerieProposalStatus)
  public total: number = 0
  public page: number = 1
  public limit: number = 10
  public pageCount: number = 0
  private serieProposal: SerieProposal
  private serieProposalId: number
  private serieProposalItems: SerieProposalItem[] = []
  public activeTab: string = 'general'
  constructor(
    private store: Store<AppState>,
    public serieProposalService: SerieProposalService,
    private router: Router,
    private notificationService: NotificationService,
    private actions$: Actions,
    public messageService: MessageService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.isLoading$ = this.store
      .select(serieProposalSelectors.selectIsLoading)
      .pipe(startWith(false))

    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.serieProposalId$ = this.store
      .select(serieProposalSelectors.selectIdRouteParam)
      .pipe(
        filter((id) => Boolean(id)),
        tap((id) => (this.serieProposalId = id))
      )

    this.serieProposal$ = this.serieProposalId$.pipe(
      tap((id) =>
        this.store.dispatch(
          serieProposalActions.fetchOneById({
            id,
            params: RequestQueryBuilder.create({
              join: [
                { field: 'customer' },
                { field: 'organization' },
                { field: 'organization.tags' },
                { field: 'serieProposalItems' },
                { field: 'linkedProduct' },
                { field: 'linkedProduct.translations' },
                { field: 'product' },
                { field: 'product.translations' },
                { field: 'product.location' },
                { field: 'messages' },
                { field: 'messages.memberSender' },
                { field: 'messages.memberRecipient' }
              ]
            })
          })
        )
      ),
      switchMap((serieProposalId) => {
        this.store.dispatch(
          BreadCrumbActions.initHistory({
            pageName: 'serieProposalModule.serieProposals',
            url: '/bookings',
            titleName: [
              WCAG_TITLE_TRANSLATIONS['serieProposals'][this.activeTab]
            ],
            entityId: Number(serieProposalId),
            resourceType: 'serieProposals'
          })
        )
        return this.store.select(
          serieProposalSelectors.selectById,
          serieProposalId
        )
      }),
      tap((serieProposal) => {
        this.store
          .select(serieProposalItemSelectors.selectByFilter, {
            field: 'serieProposalId',
            value: this.serieProposalId
          })
          .pipe(
            first((serieProposalItems) => Boolean(serieProposalItems)),
            take(1)
          )
          .subscribe(
            (serieProposalItems) =>
              (this.serieProposalItems = serieProposalItems)
          )

        return (this.serieProposal = serieProposal)
      })
    )

    this.serieProposalItemEntities$ = this.store.select(
      serieProposalItemSelectors.selectEntities
    )

    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)
  }
  public setActiveTab(activeTab: string) {
    this.activeTab = activeTab
    this.serieProposalId$.pipe(take(1)).subscribe((serieProposalId) => {
      this.store.dispatch(
        BreadCrumbActions.initHistory({
          pageName: 'serieProposalModule.serieProposals',
          url: '/bookings',
          titleName: [
            WCAG_TITLE_TRANSLATIONS['serieProposals'][this.activeTab]
          ],
          entityId: Number(serieProposalId),
          resourceType: 'serieProposals'
        })
      )
    })
  }
  public setStatus(status: SerieProposalStatus, serieProposal: SerieProposal) {
    this.memberEntities$.pipe(take(1)).subscribe((memberEntities) => {
      switch (status) {
        case SerieProposalStatus.ACCEPTED:
          this.store.dispatch(
            serieProposalActions.updateOneById({
              id: serieProposal.id,
              params: {
                status: SerieProposalStatus.ACCEPTED
              }
            })
          )
          break

        case SerieProposalStatus.DECLINED:
          this.store.dispatch(
            serieProposalActions.updateOneById({
              id: serieProposal.id,
              params: {
                status: SerieProposalStatus.DECLINED
              }
            })
          )
          break
      }

      let amountOfActions = 0

      this.actions$
        .pipe(
          ofType(serieProposalItemActions.upsertMany),
          take(
            this.serieProposalItems.filter(
              (item) => item.status != SerieProposalItemStatus.DECLINED
            ).length
          ),
          timeout(10000),
          catchError((error) => of(null))
        )
        .subscribe((action) => {
          if (action) {
            amountOfActions++

            if (
              amountOfActions ==
              this.serieProposalItems.filter(
                (item) => item.status != SerieProposalItemStatus.DECLINED
              ).length
            ) {
              this.notificationService.emit('alerts.success', 'success')
            }
          }
        })
    })
  }

  public onClickShowInitialRequest(serieProposal: SerieProposal) {
    this.modalService.show(SerieProposalInitialRequestModalComponent, {
      initialState: {
        serieProposal
      },
      class: 'modal-lg'
    })
  }
}
